import { AxiosError } from 'axios';
import { useEffect, useState, useRef } from 'react';
import { sleep } from '@atlassian/cssxp-utils-generic';
import find from 'lodash/find';
import get from 'lodash/get';

interface ReadableErrorMap {
  status: number;
  message: string;
}

export interface FieldErrorMap {
  [key: string]: string;
}

export const useFormSubmit = <R, T>({
  onComplete,
  onFailure,
  submitFormAction,
  readableErrorMap,
  detailedErrorMessage,
}: {
  onComplete?: (responsePayload?: Record<string, any>) => void;
  submitFormAction: (request: R) => Promise<T>;
  onFailure?: (errorMessage?: any) => void | Promise<void>;
  readableErrorMap?: ReadableErrorMap[];
  detailedErrorMessage?: (error: any) => string;
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>();
  const [requestTraceId, setRequestTraceId] = useState<string>('');
  const [serviceName, setServiceName] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>();
  const [request, setRequest] = useState<R>();
  const [data, setData] = useState<T>();
  const [complete, setComplete] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    // Execute
    isMounted && isMounted.current && submitFormEffect();

    // Clean  up
    return () => {
      setRequest(undefined);
    };

    // Function definitions
    async function submitFormEffect() {
      if (request) {
        setIsProcessing(true);
        setError(undefined);
        setFieldErrors(undefined);
        setData(undefined);
        try {
          const response = await submitFormAction(request);
          setData(response);
          await sleep(2000);
          /* istanbul ignore next */
          setComplete(true);
          /* istanbul ignore next */
          onComplete && onComplete(response as Record<string, any>);
        } catch (submitError) {
          const { response, message }: AxiosError = submitError as AxiosError<any>;
          // Obtaining the service name via the base URL in the configuration of response, for example: "/css-xp-ans/api/1/oms"
          const service = response?.config['baseURL']?.split('/')[1] ?? 'unknown';
          const traceId = response?.headers['x-req-trace-id'] ?? 'unknown';
          setRequestTraceId(traceId);
          setServiceName(service);
          let errorMessage = '';
          if (readableErrorMap) {
            const readableMessageWithStatus = find(readableErrorMap, ['status', response?.status]);
            errorMessage = get(readableMessageWithStatus, 'message', '');
          }

          /**
           * this method is useful when we need to construct a detailed error message
           * from the error response, this is different from the readableErrorMap as it
           * allows for more detailed error messages to be constructed and gives control
           * to the consumer of the hook to decide how to display the error message
           */
          if (detailedErrorMessage) {
            errorMessage = detailedErrorMessage(submitError);
          }
          if (errorMessage.length === 0) {
            // @ts-ignore
            errorMessage = response?.data?.message ?? message;
          }

          // @ts-ignore
          const fieldErrors = response?.data?.fieldErrors ?? null;

          setError(errorMessage);
          if (onFailure) {
            await onFailure(errorMessage);
          }

          if (fieldErrors) {
            setFieldErrors(fieldErrors);
          }
        }
        setIsProcessing(false);
      }
    }
  }, [request]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      setRequest(undefined);
      setData(undefined);
      setError(undefined);
      setComplete(false);
      setFieldErrors(undefined);
    };
  }, []);

  return { isProcessing, submitForm: setRequest, data, error, requestTraceId, serviceName, complete, fieldErrors };
};
